import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import ToggleButton from '@mui/material/ToggleButton'
import CardActionArea from '@mui/material/CardActionArea'
import Skeleton from '@mui/material/Skeleton'
import Link from '@mui/material/Link'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded'
import { motion } from 'framer-motion'
import {
  PLAN_ALLOWANCE_FOOTNOTE,
  TIER_CATALOGUE,
  findPrice,
  formatPrice,
} from './planCatalogue'
import { signupPalette } from './signupTheme'

// Plan picker used on the signup form (step 2) and again on the payment step
// if the user cancels checkout and wants to switch plan.
//
// value/onChange work in Stripe lookup keys, e.g. 'premium_monthly'.
// plans = live prices from GET /billing/plans; plansLoaded=false shows
// skeleton prices while they stream in.
const PlanSelection = ({ value, onChange, plans, plansLoaded, compact }) => {
  const selectedTierKey = value ? value.split('_')[0] : null

  // The toggle needs a cadence even before a tier is picked.
  const [fallbackInterval, setFallbackInterval] = React.useState('monthly')
  const effectiveInterval = value?.endsWith('_annually')
    ? 'annually'
    : value
      ? 'monthly'
      : fallbackInterval

  const handleInterval = (_, newInterval) => {
    if (!newInterval || newInterval === effectiveInterval) return
    setFallbackInterval(newInterval)
    if (selectedTierKey) onChange(`${selectedTierKey}_${newInterval}`)
  }

  const annualSavingsLabel = (tierKey) => {
    const monthly = findPrice(plans, tierKey, 'monthly')
    const annually = findPrice(plans, tierKey, 'annually')
    if (
      typeof monthly?.unit_amount !== 'number' ||
      typeof annually?.unit_amount !== 'number' ||
      monthly.unit_amount <= 0
    ) {
      return null
    }
    const yearAtMonthly = monthly.unit_amount * 12
    if (annually.unit_amount >= yearAtMonthly) return null
    const pct = Math.round(
      ((yearAtMonthly - annually.unit_amount) / yearAtMonthly) * 100,
    )
    return pct > 0 ? `Save ${pct}%` : null
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={1}
      >
        <Typography variant="body2" color="text.secondary">
          All plans are billed by subscription — no free trial. Prices exclude
          VAT.
        </Typography>
        <ToggleButtonGroup
          value={effectiveInterval}
          exclusive
          onChange={handleInterval}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 2,
              textTransform: 'none',
              fontWeight: 600,
            },
            '& .Mui-selected': {
              bgcolor: `${signupPalette.primary.main} !important`,
              color: '#fff !important',
            },
          }}
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="annually">Annually</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: compact ? '1fr' : 'repeat(3, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {TIER_CATALOGUE.map((tierMeta, index) => {
          const lookupKey = `${tierMeta.key}_${effectiveInterval}`
          const selected = value === lookupKey
          const price = findPrice(plans, tierMeta.key, effectiveInterval)
          const display = formatPrice(price, effectiveInterval)
          const savings =
            effectiveInterval === 'annually'
              ? annualSavingsLabel(tierMeta.key)
              : null

          return (
            <motion.div
              key={tierMeta.key}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              style={{ display: 'flex' }}
            >
              <CardActionArea
                onClick={() => onChange(lookupKey)}
                sx={{
                  borderRadius: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  border: '2px solid',
                  borderColor: selected
                    ? signupPalette.third.main
                    : 'rgba(36,47,64,0.12)',
                  bgcolor: selected ? 'rgba(35,154,164,0.05)' : '#fff',
                  boxShadow: selected
                    ? '0 10px 30px rgba(35,154,164,0.18)'
                    : '0 4px 16px rgba(36,47,64,0.06)',
                  transition: 'all 0.2s ease',
                  p: 2,
                  position: 'relative',
                }}
              >
                {tierMeta.popular && (
                  <Chip
                    label="Most popular"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      bgcolor: signupPalette.third.main,
                      color: '#fff',
                      fontWeight: 700,
                    }}
                  />
                )}

                <Stack spacing={1.25} sx={{ width: '100%', flexGrow: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {selected ? (
                      <CheckCircleRoundedIcon
                        sx={{ color: signupPalette.third.main }}
                      />
                    ) : (
                      <RadioButtonUncheckedRoundedIcon
                        sx={{ color: 'rgba(36,47,64,0.30)' }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.1rem',
                        color: signupPalette.primary.main,
                      }}
                    >
                      {tierMeta.name}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    {tierMeta.description}
                  </Typography>

                  <Box>
                    {display ? (
                      <Stack
                        direction="row"
                        alignItems="baseline"
                        spacing={0.5}
                        flexWrap="wrap"
                      >
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: '1.7rem',
                            color: signupPalette.primary.main,
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {display.symbol}
                          {display.amount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          / {display.per}
                        </Typography>
                        {savings && (
                          <Chip
                            label={savings}
                            size="small"
                            sx={{
                              ml: 0.5,
                              bgcolor: 'rgba(35,154,164,0.12)',
                              color: signupPalette.third.dark,
                              fontWeight: 700,
                            }}
                          />
                        )}
                      </Stack>
                    ) : plansLoaded ? (
                      <Typography variant="body2" color="text.secondary">
                        Price shown at checkout
                      </Typography>
                    ) : (
                      <Skeleton width={110} height={38} />
                    )}
                  </Box>

                  {/* The longer allowance lines wrap inside a narrow card
                      rather than being cut off with an ellipsis. */}
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {tierMeta.limits.map((limit) => (
                      <Chip
                        key={limit}
                        label={limit}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.7rem',
                          height: 'auto',
                          minHeight: 24,
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            py: '3px',
                          },
                        }}
                      />
                    ))}
                  </Stack>

                  <Stack spacing={0.5} sx={{ pt: 0.5 }}>
                    {tierMeta.features.map((feature) => (
                      <Stack
                        key={feature}
                        direction="row"
                        spacing={0.75}
                        alignItems="flex-start"
                      >
                        <CheckRoundedIcon
                          sx={{
                            fontSize: 15,
                            mt: '3px',
                            color: signupPalette.third.main,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}
                          color="text.secondary"
                        >
                          {feature}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </CardActionArea>
            </motion.div>
          )
        })}
      </Box>

      {/* Explains the asterisk on every allowance pill above. */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', lineHeight: 1.6 }}
      >
        {PLAN_ALLOWANCE_FOOTNOTE} Read the current{' '}
        <Link
          href="/subscription-agreement"
          target="_blank"
          rel="noopener"
          sx={{ color: signupPalette.third.dark, fontWeight: 600 }}
        >
          AmbuNet Business Subscription Agreement
        </Link>
        .
      </Typography>
    </Stack>
  )
}

export default PlanSelection
